/**
 * O catálogo de plugins oficiais no cliente (docs/spec/web-plugins.md, "Plugins oficiais"): a lista vem
 * do servidor de novo cada vez que a aba Oficiais ou a página Plugins abrem (é pequena, e uma versão
 * publicada agora já aparece), mostrando a de antes enquanto isso; e cada oficial é comparado com os
 * plugins da mesa para dizer se instala, atualiza ou já está lá.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { api } from '../../api.js';
import { servesSystem, type OfficialPlugin, type RoomPlugin } from '../../shared.js';
import { describeError, useApp } from '../../store.js';
import { compareVersions, localize } from './sdk.js';

type CatalogStore = {
  /** null até a primeira resposta */
  plugins: OfficialPlugin[] | null;
  error: string | null;
  loading: boolean;
  load: () => Promise<void>;
};

export const useOfficialCatalog = create<CatalogStore>()((set, get) => ({
  plugins: null,
  error: null,
  loading: false,
  async load() {
    const { token } = useApp.getState();
    // dois pedidos ao mesmo tempo (a página e o diálogo) viram um
    if (!token || get().loading) return;
    set({ loading: true, error: null });
    try {
      const { plugins } = await api.officialPlugins(token);
      set({ plugins, loading: false });
    } catch (error) {
      set({ error: describeError(error), loading: false });
    }
  },
}));

/** O catálogo, pedido ao montar (`enabled`: só quem instala precisa dele). */
export function useOfficialPlugins(enabled = true): Pick<CatalogStore, 'plugins' | 'error' | 'loading'> & { retry: () => void } {
  const plugins = useOfficialCatalog((s) => s.plugins);
  const error = useOfficialCatalog((s) => s.error);
  const loading = useOfficialCatalog((s) => s.loading);
  useEffect(() => {
    if (enabled) void useOfficialCatalog.getState().load();
  }, [enabled]);
  return { plugins, error, loading, retry: () => void useOfficialCatalog.getState().load() };
}

/**
 * O que a mesa tem de um oficial, pelo `id` do manifesto: nada (instalar), uma versão mais velha
 * (atualizar), ou a mesma ou uma mais nova (instalado; a mais nova veio de arquivo).
 */
export type CatalogEntryState = { kind: 'install' } | { kind: 'update'; from: string } | { kind: 'installed' };

export function catalogState(entry: OfficialPlugin, plugins: readonly RoomPlugin[]): CatalogEntryState {
  const installed = plugins.find((p) => p.moduleId === entry.moduleId);
  if (!installed) return { kind: 'install' };
  return compareVersions(installed.version, entry.version) < 0 ? { kind: 'update', from: installed.version } : { kind: 'installed' };
}

/** O oficial que atualiza este plugin da mesa (o do mesmo `id`, numa versão maior), ou undefined. */
export function officialUpdate(plugin: RoomPlugin, entries: readonly OfficialPlugin[] | null): OfficialPlugin | undefined {
  const entry = entries?.find((e) => e.moduleId === plugin.moduleId);
  return entry && compareVersions(plugin.version, entry.version) < 0 ? entry : undefined;
}

/** A aba Oficiais em dois grupos: os do sistema da mesa ("Para D&D 5e") e os outros, cada um por nome. */
export function catalogGroups(entries: readonly OfficialPlugin[], roomSystem: string, lang: string): { forSystem: OfficialPlugin[]; others: OfficialPlugin[] } {
  const sorted = [...entries].sort((a, b) => localize(a.name, lang).localeCompare(localize(b.name, lang), lang));
  const forSystem = sorted.filter((entry) => servesSystem(entry.systems, roomSystem));
  return { forSystem, others: sorted.filter((entry) => !forSystem.includes(entry)) };
}
